// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  ExpandMore,
  Error
} from '@mui/icons-material';

/**
 * ReviewStep Component (Step 6 of 6)
 * 
 * Review all data before creating:
 * - Summary of basic information
 * - List of categories and metrics
 * - QA context settings
 * - QA context preview
 * - Validation status
 * - Create button
 */
const ReviewStep = ({ formData, errors }) => {
  const isBoolean = formData.criteria_type === 'boolean';

  // Calculate totals
  const totalCategoryWeight = formData.categories.reduce(
    (sum, cat) => sum + (cat.weight_percentage || 0),
    0
  );
  const totalMetrics = formData.categories.reduce(
    (sum, cat) => sum + (cat.metrics?.length || 0),
    0
  );

  // Validation status
  const categoryWeightValid = totalCategoryWeight === 100;
  const hasCategories = formData.categories.length > 0;
  const hasQualificationItems = (formData.categories?.[0]?.metrics?.length || 0) > 0;
  const hasName = formData.name && formData.name.trim().length >= 3;
  const hasTeam = formData.team_id && formData.team_id.trim().length > 0;
  
  // QA Context validation
  const hasQAContext = formData.qa_context?.conversation_type && 
                      formData.qa_context?.participants && 
                      formData.qa_context?.target_audience &&
                      formData.qa_context?.conversation_objective &&
                      formData.qa_context?.ideal_approach;
  const hasQAPreview = formData.qa_context_preview?.preview;

  const isValid = isBoolean
    ? (hasQualificationItems && hasName && hasTeam && hasQAContext && hasQAPreview)
    : (categoryWeightValid && hasCategories && hasName && hasTeam && hasQAContext && hasQAPreview);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 6: Review & Create
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review your QA criteria before creating. Make sure all information is correct.
      </Typography>

      {/* Validation Status */}
      <Alert 
        severity={isValid ? 'success' : 'warning'} 
        icon={isValid ? <CheckCircle /> : <Warning />}
        sx={{ mb: 3 }}
      >
        <Typography variant="body2" fontWeight="medium">
          {isValid 
            ? 'All validations passed! You can now create this criteria.' 
            : 'Please fix the validation errors before creating.'}
        </Typography>
      </Alert>

      {/* Basic Information */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formData.name || <em>Not set</em>}
                {!hasName && <Chip label="Invalid" size="small" color="error" sx={{ ml: 1 }} />}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Team
              </Typography>
              <Typography variant="body2">
                {formData.team_id ? <Chip label="Team selected" size="small" color="primary" variant="outlined" /> : <em>Not set</em>}
                {!hasTeam && <Chip label="Invalid" size="small" color="error" sx={{ ml: 1 }} />}
              </Typography>
            </Box>

            {formData.description && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body2">
                  {formData.description}
                </Typography>
              </Box>
            )}

            <Stack direction="row" spacing={2}>
              <Chip
                label={formData.is_active ? 'Active' : 'Inactive'}
                size="small"
                color={formData.is_active ? 'success' : 'default'}
              />
              {formData.is_default && (
                <Chip label="Default" size="small" color="warning" />
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Categories Summary or Qualification Items Summary */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              {isBoolean ? 'Qualification Boolean' : 'Categories & Metrics'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isBoolean ? (
                <Chip label={`${totalMetrics} items`} size="small" />
              ) : (
                <>
                  <Chip label={`${formData.categories.length} categories`} size="small" />
                  <Chip label={`${totalMetrics} metrics`} size="small" />
                  <Chip 
                    label={`${totalCategoryWeight}%`}
                    size="small"
                    color={categoryWeightValid ? 'success' : 'error'}
                    icon={categoryWeightValid ? undefined : <Error />}
                  />
                </>
              )}
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {isBoolean ? (
            !hasQualificationItems ? (
              <Alert severity="error">
                No qualification items defined. Please go back and add at least one qualification item.
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="5%">#</TableCell>
                      <TableCell width="18%">Qualification Name</TableCell>
                      <TableCell width="35%">Qualification Question</TableCell>
                      <TableCell width="22%">Qualification Answer</TableCell>
                      <TableCell width="10%">Mandatory</TableCell>
                      <TableCell width="10%">Fail conditions / Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(formData.categories?.[0]?.metrics || []).map((metric, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{metric.metric_name || '-'}</TableCell>
                        <TableCell>{metric.description || <em>Unset</em>}</TableCell>
                        <TableCell>
                          {metric.keywords && metric.keywords.length > 0 ? metric.keywords.join(', ') : '-'}
                        </TableCell>
                        <TableCell>{metric.is_mandatory ? 'Yes' : 'No'}</TableCell>
                        <TableCell>{metric.evaluation_guide ? metric.evaluation_guide : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : !hasCategories ? (
            <Alert severity="error">
              No categories defined. Please go back and add at least one category.
            </Alert>
          ) : (
            <Box>
              {formData.categories.map((category, catIndex) => {
                const totalMetricWeight = (category.metrics || []).reduce(
                  (sum, metric) => sum + (metric.weight_percentage || 0),
                  0
                );
                const metricWeightValid = totalMetricWeight === category.weight_percentage;

                return (
                  <Accordion key={catIndex} defaultExpanded={catIndex === 0}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {category.category_name || <em>Unnamed Category</em>}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip label={`${category.weight_percentage}%`} size="small" variant="outlined" />
                          <Chip 
                            label={`${category.metrics?.length || 0} metrics`} 
                            size="small" 
                            variant="outlined"
                          />
                          {!metricWeightValid && (
                            <Chip label="Weight Error" size="small" color="error" />
                          )}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {!category.metrics || category.metrics.length === 0 ? (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                          No metrics defined for this category
                        </Alert>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell width="5%">#</TableCell>
                                <TableCell width="40%">Metric Name</TableCell>
                                <TableCell width="15%">Weight</TableCell>
                                <TableCell width="40%">Keywords</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {category.metrics.map((metric, metricIndex) => (
                                <TableRow key={metricIndex}>
                                  <TableCell>{metricIndex + 1}</TableCell>
                                  <TableCell>{metric.metric_name || <em>Unnamed</em>}</TableCell>
                                  <TableCell>
                                    <Chip label={`${metric.weight_percentage}%`} size="small" />
                                  </TableCell>
                                  <TableCell>
                                    {metric.keywords && metric.keywords.length > 0 
                                      ? metric.keywords.join(', ') 
                                      : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                      {!metricWeightValid && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          Metric weights must total {category.weight_percentage}% (category weight) - currently {totalMetricWeight}%
                        </Alert>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* QA Context Settings */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            QA Context Settings
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Conversation Type
              </Typography>
              <Typography variant="body1">
                {formData.qa_context?.conversation_type || <em>Not specified</em>}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Participants
              </Typography>
              <Typography variant="body1">
                {formData.qa_context?.participants || <em>Not specified</em>}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Target Audience
              </Typography>
              <Typography variant="body1">
                {formData.qa_context?.target_audience || <em>Not specified</em>}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Conversation Objective
              </Typography>
              <Typography variant="body1">
                {formData.qa_context?.conversation_objective || <em>Not specified</em>}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Ideal Approach
              </Typography>
              <Typography variant="body1">
                {formData.qa_context?.ideal_approach || <em>Not specified</em>}
              </Typography>
            </Box>
            {formData.qa_context?.context_notes && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Additional Context Notes
                </Typography>
                <Typography variant="body1">
                  {formData.qa_context.context_notes}
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* QA Context Preview */}
      {formData.qa_context_preview?.preview && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              QA Context Preview
            </Typography>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography 
                variant="body2" 
                component="pre" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  margin: 0
                }}
              >
                {formData.qa_context_preview.preview}
              </Typography>
            </Paper>
            {formData.qa_context_preview.processing_time && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Generated in {formData.qa_context_preview.processing_time.toFixed(2)}s
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Final Validation Errors */}
      {!isValid && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            Please fix the following issues:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {!hasName && <li>Criteria name must be at least 3 characters</li>}
            {!hasTeam && <li>Team is required</li>}
            {isBoolean ? (
              !hasQualificationItems && <li>At least one qualification item is required</li>
            ) : (
              <>
                {!hasCategories && <li>At least one category is required</li>}
                {!categoryWeightValid && <li>Category weights must total 100%</li>}
              </>
            )}
            {!hasQAContext && <li>QA context settings must be completed</li>}
            {!hasQAPreview && <li>QA context preview must be generated</li>}
          </ul>
        </Alert>
      )}
    </Box>
  );
};

export default ReviewStep;
